import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';

const EnhancedSidebar = ({ expanded = true, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [hoveredItem, setHoveredItem] = useState(null);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth < 1024 && expanded) { // Only on mobile when expanded
        const sidebar = document.querySelector('[data-sidebar]');
        if (sidebar && !sidebar.contains(event.target)) {
          onToggle();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded, onToggle]);

  // Navigation structure - ServiceReviewAI focused menu (7 core items)
  const navigationStructure = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard'
    },
    {
      label: 'CRM Pipeline',
      path: '/crm',
      icon: 'GitBranch'
    },
    {
      label: 'Client Management',
      path: '/clients',
      icon: 'Users'
    },
    {
      label: 'Campaigns',
      path: '/campaigns',
      icon: 'Megaphone'
    },
    {
      label: 'Reviews',
      path: '/reviews',
      icon: 'Star'
    },
    {
      label: 'Instant Request',
      path: '/instant-request',
      icon: 'Zap'
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: 'Building'
    }
  ];

  // All users now have access to all navigation items
  const visibleNavigation = navigationStructure;

  const handleNavigation = (path) => {
    navigate(path);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024 && expanded) {
      onToggle();
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderNavigationItem = (item) => {
    const active = isActive(item.path);
    
    return (
      <li key={item.path || item.label}>
        <div className="relative">
          <Button
            variant="ghost"
            justify={expanded ? 'start' : 'center'}
            onClick={() => handleNavigation(item.path)}
            onMouseEnter={() => setHoveredItem(expanded ? null : item.label)}
            onMouseLeave={() => setHoveredItem(null)}
            className={`
              w-full h-10 flex items-center transition-micro relative
              ${expanded ? 'px-3 space-x-3' : 'px-4'}
              ${active 
                ? 'bg-sidebar-primary/10 text-sidebar-primary border-r-2 border-sidebar-primary' 
                : 'text-sidebar-foreground hover:bg-sidebar-accent/10 hover:text-sidebar-foreground'
              }
            `}
          >
            <Icon 
              name={item.icon} 
              size={18} 
              className={`flex-shrink-0 ${active ? 'text-sidebar-primary' : 'inherit'}`} 
            />
            {expanded && (
              <span className="font-medium text-sm truncate">
                {item.label}
              </span>
            )}
          </Button>

          {/* Tooltip for collapsed state */}
          {!expanded && hoveredItem === item.label && (
            <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 bg-text-primary text-white text-sm rounded whitespace-nowrap z-300">
              {item.label}
              <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-text-primary"></div>
            </div>
          )}
        </div>
      </li>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {expanded && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onToggle}></div>
      )}

      <aside 
        data-sidebar
        className={`
          fixed top-0 left-0 h-full bg-sidebar border-r border-sidebar-border z-100 transition-all duration-300 ease-in-out shadow-lg
          ${expanded ? 'w-64' : 'w-16'}
          ${expanded ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo Section */}
        <div className="h-16 flex items-center justify-center border-b border-sidebar-border px-4">
          {expanded ? (
            <div className="flex items-center justify-center w-full">
              <img 
                src="/assets/images/servicereviewai-logo.svg" 
                alt="ServiceReviewAI" 
                className="h-10 w-auto max-w-[200px] object-contain"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <img 
                src="/assets/images/servicereviewai-logo-compact.svg" 
                alt="ServiceReviewAI" 
                className="h-8 w-8 object-contain"
              />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {visibleNavigation.map((navItem) => renderNavigationItem(navItem))}
          </ul>
        </nav>

        {/* Toggle Button */}
        {onToggle && (
          <div className="p-3 border-t border-sidebar-border">
            <Button
              variant="ghost"
              justify={expanded ? 'between' : 'center'}
              onClick={onToggle}
              className={`
                w-full h-10 flex items-center transition-micro
                ${expanded ? 'px-3' : 'px-0'}
                text-text-secondary hover:text-text-primary hover:bg-surface-hover
              `}
            >
              {expanded && (
                <span className="text-sm font-medium">Collapse</span>
              )}
              <Icon 
                name={expanded ? "ChevronLeft" : "ChevronRight"} 
                size={16} 
                className="transition-transform duration-300" 
              />
            </Button>
          </div>
        )}

        {/* User Status Indicator */}
        {expanded && (
          <div className="p-3 border-t border-border">
            <div className="flex items-center space-x-2 text-xs text-text-muted">
              <div className="w-2 h-2 bg-success rounded-full"></div>
              <span>Online</span>
              <span className="ml-auto">v2.1.0</span>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default EnhancedSidebar;